import { Urgency } from './types';

export type InjuryType = 'commercial' | 'ohio_bwc';
export type Laterality = 'left' | 'right' | 'bilateral';
export type SiteOfService = 'hospital' | 'asc' | 'office';
export type SpecialtyDomain = 'orthopedics' | 'podiatry';

/** HCPCS Q-codes (skin substitute series) trigger the Wound Care Matrix gate. */
export const isQCode = (cpt: string): boolean => /^Q\d{4}$/i.test(cpt.trim());

/** Podiatry digit modifiers (toes). */
export const PODIATRY_DIGIT_MODIFIERS = [
  'TA', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9',
] as const;
export const isPodiatryDigitModifier = (mod: string): boolean =>
  (PODIATRY_DIGIT_MODIFIERS as readonly string[]).includes(mod.toUpperCase());

export interface WizardPatient {
  id: string;
  mrn: string;
  firstName: string;
  lastName: string;
  dob: string;
  // FHIR source tracking
  fhirId?: string;
  fromFHIR?: boolean;
}

export interface WizardCoverage {
  payerId: string;
  payerName: string;
  planName?: string;
  memberId?: string;
  groupNumber?: string;
  // FHIR source tracking
  fhirId?: string;
  fromFHIR?: boolean;
}

export interface BWCInfo {
  claimNumber: string;
  dateOfInjury: string;
}

export interface SelectedProcedure {
  cptCode: string;
  description: string;
  laterality: Laterality;
  modifiers: string[];
}

// Ancillary procedure for authorization subset
export interface AncillaryProcedure {
  cptCode: string;
  description: string;
  isSelected: boolean;
}

// Surgeon attestation for authorization subset
export interface SurgeonAttestation {
  attested: boolean;
  timestamp: string | null;
}

export interface DiagnosisCode {
  code: string;
  description: string;
  // FHIR source tracking
  fhirId?: string;
  fromFHIR?: boolean;
}

export interface RequiredDocument {
  id: string;
  name: string;
  isProvided: boolean;
  isRequired: boolean;
}

// FHIR launch context
export interface FHIRLaunchContext {
  patientId?: string;
  encounterId?: string;
  practitionerId?: string;
  fhirBaseUrl?: string;
  launchedFromEHR: boolean;
}

export interface WizardState {
  // Step 1: Patient & Coverage
  patient: WizardPatient | null;
  coverage: WizardCoverage | null;
  injuryType: InjuryType;
  bwcInfo: BWCInfo | null;
  
  // Step 1 (continued): Procedures & Diagnoses
  procedures: SelectedProcedure[];
  siteOfService: SiteOfService | null;
  diagnosisCodes: DiagnosisCode[];
  urgency: Urgency;
  scheduledDate: string | null;
  
  // Authorization Subset (Ancillary Procedures)
  ancillaryProcedures: AncillaryProcedure[];
  surgeonAttestation: SurgeonAttestation;
  // Subset template tracking for persistence
  subsetTemplateId: string | null;
  subsetTemplateVersion: number | null;
  
  // Step 3: Authorization Summary
  requiredDocuments: RequiredDocument[];
  authRequired: 'yes' | 'no' | 'conditional';
  notes: string;
  modifierReviewRequired: boolean;
  
  // FHIR/Epic integration
  fhirContext: FHIRLaunchContext | null;

  // Specialty (drives modifier sets, CPT filtering, AI extractor selection)
  specialtyDomain: SpecialtyDomain;
}

export const initialWizardState: WizardState = {
  patient: null,
  coverage: null,
  injuryType: 'commercial',
  bwcInfo: null,
  procedures: [],
  siteOfService: null,
  diagnosisCodes: [],
  urgency: 'routine',
  scheduledDate: null,
  ancillaryProcedures: [],
  surgeonAttestation: { attested: false, timestamp: null },
  subsetTemplateId: null,
  subsetTemplateVersion: null,
  requiredDocuments: [],
  authRequired: 'yes',
  notes: '',
  modifierReviewRequired: false,
  fhirContext: null,
  specialtyDomain: 'orthopedics',
};

export interface WizardContextType {
  state: WizardState;
  updateState: (updates: Partial<WizardState>) => void;
  resetWizard: () => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  canProceed: boolean;
  canProceedStrict: boolean; // Strict validation (without demo mode bypass)
  // FHIR integration helpers
  applyFHIRContext: (context: {
    patient?: WizardPatient | null;
    coverage?: WizardCoverage | null;
    diagnoses?: DiagnosisCode[];
    launchContext?: FHIRLaunchContext;
  }) => void;
  hasFHIRContext: boolean;
  // Demo mode
  isDemoMode: boolean;
  activateDemoMode: (type?: 'commercial' | 'bwc' | 'podiatry') => void;
  // Specialty domain switcher
  setSpecialtyDomain: (domain: SpecialtyDomain) => void;
  // Auto-fill support
  autoFillAndProceed: () => { fieldsAutoFilled: string[]; updatedState: Record<string, unknown> };
  lastAutoFilledFields: string[];
}
